import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCrown, faLock, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { usePlan } from '../../../../context/PlanContext';
import { useBusinessContext } from '../../../../context/BusinessContext';
import {
    getAiMonthlyUsage, monthlyPriceFor,
    AiMonthlyUsage, PlanFeatureKey, PlanKey, PlanView,
} from '../../../../service/plan/plan-service';
import './PlanComponent.css';

const FEATURE_ORDER: PlanFeatureKey[] = [
    'TEAM', 'COMMISSIONS', 'WAITLIST', 'CHAT',
    'FINANCE', 'REPORTS', 'ROLES', 'CONVENIOS', 'EVENTS', 'WAITING_ROOM',
];
const PLAN_ORDER: PlanKey[] = ['AUTONOMO', 'PRO'];
const UPGRADE_EMAIL = 'contato@getservice.io';

/**
 * Settings → Plano: what the business pays today, how much of the plan it is
 * using, and the catalog with a per-professional calculator. Billing is handled
 * by the GetService team for now (e-mail); self-service Stripe Billing is next.
 */
const PlanComponent: React.FC = () => {
    const { t } = useTranslation();
    const { plan, catalog, loading, reload } = usePlan();
    const { selectedBusiness } = useBusinessContext();
    const { getAccessTokenSilently } = useAuth0();
    const [aiUsage, setAiUsage] = useState<AiMonthlyUsage | null>(null);
    /** Calculator on the PRO card. Kept as TEXT so the field can be cleared while
     *  typing (clamping on every keystroke made "1" impossible to delete). */
    const [simProsText, setSimProsText] = useState<string>('1');
    /** Include the add-on in the simulated total. */
    const [simAi, setSimAi] = useState<boolean>(false);
    const simPros = Math.min(99, Math.max(1, parseInt(simProsText, 10) || 1));

    useEffect(() => {
        if (plan) {
            setSimProsText(String(Math.max(1, plan.teamMemberCount || 1)));
            setSimAi(plan.aiAddon);
        }
    }, [plan]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!selectedBusiness?.id || !plan?.aiAddon) { setAiUsage(null); return; }
            try {
                const token = await getAccessTokenSilently();
                const u = await getAiMonthlyUsage(selectedBusiness.id, token);
                if (!cancelled) setAiUsage(u);
            } catch {
                if (!cancelled) setAiUsage(null);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedBusiness?.id, plan?.aiAddon, getAccessTokenSilently]);

    if (loading && !plan) {
        return <p className="text-muted fst-italic p-3">{t('common.loading', 'Carregando…')}</p>;
    }
    if (!plan) {
        return (
            <div className="business-section-container separated-container">
                <p className="text-muted">{t('plan.page.unavailable')}</p>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={reload}>
                    {t('common.retry', 'Tentar de novo')}
                </button>
            </div>
        );
    }

    const teamPct = plan.teamCap > 0 ? Math.min(100, Math.round((plan.teamMemberCount / plan.teamCap) * 100)) : 0;
    const aiPct = aiUsage && aiUsage.budgetBrl > 0 ? Math.min(100, Math.round((aiUsage.spentBrl / aiUsage.budgetBrl) * 100)) : 0;
    const mailto = (subject: string) =>
        `mailto:${UPGRADE_EMAIL}?subject=${encodeURIComponent(subject)}`;

    return (
        <div className="side-content-vertical" data-cy="plan-page">
            {/* ---------- Current plan ---------- */}
            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('plan.page.title')}</h3>
                <div className="gs-plan-current">
                    <div className="gs-plan-current__head">
                        <span className="gs-plan-current__name" data-cy="plan-current-name">
                            {t(`plan.names.${plan.plan}`)}
                        </span>
                        {plan.founder && (
                            <span className="gs-plan-badge gs-plan-badge--founder">
                                <FontAwesomeIcon icon={faCrown} /> {t('plan.page.founder')}
                            </span>
                        )}
                        <span className={`gs-plan-badge gs-plan-badge--${plan.status.toLowerCase()}`}>
                            {t(`plan.status.${plan.status}`, plan.status)}
                        </span>
                        {!plan.enforcing && <span className="gs-plan-badge gs-plan-badge--log">{t('plan.page.logMode')}</span>}
                    </div>

                    <div className="gs-plan-current__price" data-cy="plan-monthly-price">
                        {plan.founder
                            ? t('plan.page.founderNoCharge')
                            : plan.monthlyPriceBrl > 0
                                ? t('plan.page.pricePerMonth', { price: plan.monthlyPriceBrl })
                                : t('plan.page.free')}
                        {plan.currentPeriodEnd && !plan.founder && (
                            <span className="gs-plan-current__period"> · {t('plan.page.renews', { date: plan.currentPeriodEnd.slice(0, 10) })}</span>
                        )}
                    </div>

                    {/* Price breakdown — only when there is something to break down. */}
                    {!plan.founder && plan.monthlyPriceBrl > 0 && (
                        <ul className="gs-plan-breakdown">
                            <li>
                                <span>{t('plan.page.breakdownBase', { plan: t(`plan.names.${plan.plan}`) })}</span>
                                <span>R$ {plan.monthlyPriceBrl - plan.extraProfessionals * plan.extraProfessionalBrl - (plan.aiAddon ? plan.aiAddonBrl : 0)}</span>
                            </li>
                            {plan.extraProfessionals > 0 && (
                                <li>
                                    <span>{t('plan.page.breakdownSeats', { count: plan.extraProfessionals, price: plan.extraProfessionalBrl })}</span>
                                    <span>R$ {plan.extraProfessionals * plan.extraProfessionalBrl}</span>
                                </li>
                            )}
                            {plan.aiAddon && (
                                <li>
                                    <span>{t('plan.page.breakdownAi')}</span>
                                    <span>R$ {plan.aiAddonBrl}</span>
                                </li>
                            )}
                        </ul>
                    )}

                    <div className="gs-plan-usage">
                        <div className="gs-plan-usage__row">
                            <span className="gs-plan-usage__label">{t('plan.page.professionals')}</span>
                            <span className="gs-plan-usage__value" data-cy="plan-team-usage">
                                {plan.teamCap > 0
                                    ? t('plan.page.ofCap', { used: plan.teamMemberCount, cap: plan.teamCap })
                                    : t('plan.page.unlimitedUsed', { used: plan.teamMemberCount })}
                            </span>
                        </div>
                        {plan.teamCap > 0 && (
                            <div className="gs-plan-bar">
                                <div className={`gs-plan-bar__fill ${teamPct >= 100 ? 'is-full' : ''}`} style={{ width: `${teamPct}%` }} />
                            </div>
                        )}
                        {plan.teamCap > 0 && plan.teamMemberCount >= plan.teamCap && (
                            <p className="gs-plan-usage__warn">
                                {t('plan.page.seatFull', { price: plan.extraProfessionalBrl })}
                            </p>
                        )}

                        {/* AI add-on: usage when on, offer when off. */}
                        <div className="gs-plan-usage__row">
                            <span className="gs-plan-usage__label">
                                <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('plan.page.aiBudget')}
                            </span>
                            <span className="gs-plan-usage__value" data-cy="plan-ai-usage">
                                {!plan.aiAddon
                                    ? t('plan.page.aiOff', { price: plan.aiAddonBrl })
                                    : aiUsage
                                        ? t('plan.page.aiSpent', { spent: aiUsage.spentBrl.toFixed(2), budget: plan.aiBudgetBrl })
                                        : t('plan.page.aiBudgetOnly', { budget: plan.aiBudgetBrl })}
                            </span>
                        </div>
                        {plan.aiAddon && aiUsage && (
                            <div className="gs-plan-bar">
                                <div className={`gs-plan-bar__fill ${aiPct >= 100 ? 'is-full' : ''}`} style={{ width: `${aiPct}%` }} />
                            </div>
                        )}
                        {aiUsage?.blocked && <p className="gs-plan-usage__warn">{t('plan.page.aiBlocked')}</p>}
                    </div>
                </div>
            </div>

            {/* ---------- Catalog ---------- */}
            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('plan.page.catalogTitle')}</h3>
                <p className="notif-intro">{t('plan.page.catalogIntro')}</p>

                <div className="gs-plan-grid gs-plan-grid--two">
                    {PLAN_ORDER.map((key) => {
                        const p: PlanView | undefined = catalog?.find((c) => c.key === key);
                        if (!p) return null;
                        const isCurrent = key === plan.plan;
                        return (
                            <div key={key} className={`gs-plan-card ${isCurrent ? 'is-current' : ''}`} data-cy={`plan-card-${key.toLowerCase()}`}>
                                <div className="gs-plan-card__name">
                                    {t(`plan.names.${key}`)}
                                    {/* Same crown that marks a locked feature elsewhere — it reads as
                                        "this is the paid plan" without extra copy. */}
                                    {p.paid && <FontAwesomeIcon icon={faCrown} className="gs-plan-crown" />}
                                </div>
                                <div className="gs-plan-card__price">
                                    {p.priceBrl > 0 ? <>R$ {p.priceBrl}<small>/{t('plan.page.month')}</small></> : t('plan.page.free')}
                                </div>
                                <div className="gs-plan-card__cap">
                                    {p.paid
                                        ? t('plan.page.includesFirstPro', { price: p.extraProfessionalBrl })
                                        : t('plan.page.onlyOnePro')}
                                </div>
                                <ul className="gs-plan-card__features">
                                    <li><FontAwesomeIcon icon={faCheck} /> {t('plan.features.BASE')}</li>
                                    {FEATURE_ORDER.map((f) => (
                                        <li key={f} className={p.features.includes(f) ? '' : 'is-locked'}>
                                            <FontAwesomeIcon icon={p.features.includes(f) ? faCheck : faLock} /> {t(`plan.features.${f}`)}
                                        </li>
                                    ))}
                                </ul>

                                {/* Seats and the add-on only exist on the paid plan. */}
                                {p.paid && (
                                    <>
                                    {/* The add-on lives INSIDE the paid card: it is not a third plan. */}
                                    <div className="gs-plan-addon">
                                        <div className="gs-plan-addon__head">
                                            <FontAwesomeIcon icon={faWandMagicSparkles} className="gs-plan-addon-icon" />
                                            <span className="gs-plan-addon__title">{t('plan.features.AI')}</span>
                                            <span className="gs-plan-addon__tag">{t('plan.page.addonTag')}</span>
                                            <span className="gs-plan-addon__price">+ R$ {p.aiAddonBrl}<small>/{t('plan.page.month')}</small></span>
                                        </div>
                                        <p className="gs-plan-addon__text">
                                            {t('plan.page.aiAddonCap', { budget: p.aiAddonBudgetBrl })} · {t('plan.featureHints.AI')}
                                        </p>
                                        {plan.aiAddon ? (
                                            <span className="gs-plan-addon__active">{t('plan.page.aiActive')}</span>
                                        ) : (
                                            <a
                                                className="gs-plan-addon__link"
                                                href={mailto(t('plan.page.mailSubjectAi', { business: selectedBusiness?.name ?? '' }))}
                                                data-cy="plan-enable-ai"
                                            >
                                                {t('plan.page.enableAi')}
                                            </a>
                                        )}
                                    </div>

                                    {/* Calculator: a salon owner sizes the real bill before asking. */}
                                    <div className="gs-plan-calc" data-cy="plan-calculator">
                                        <label className="gs-plan-calc__label" htmlFor="gs-plan-calc-pros">
                                            {t('plan.page.calcLabel')}
                                        </label>
                                        <div className="gs-plan-calc__row">
                                            <input
                                                id="gs-plan-calc-pros"
                                                type="text"
                                                inputMode="numeric"
                                                className="form-control form-control-sm gs-plan-calc__input"
                                                value={simProsText}
                                                onChange={(e) => setSimProsText(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
                                                onBlur={() => setSimProsText(String(simPros))}
                                            />
                                            <span className="gs-plan-calc__result" data-cy="plan-calculator-result">
                                                R$ {monthlyPriceFor(p, simPros, simAi)}<small>/{t('plan.page.month')}</small>
                                            </span>
                                        </div>
                                        <a
                                            className="btn btn-primary btn-sm gs-plan-calc__cta"
                                            href={mailto(t('plan.page.mailSubjectSeats', {
                                                business: selectedBusiness?.name ?? '',
                                                count: simPros,
                                                ai: simAi ? t('plan.page.mailWithAi') : t('plan.page.mailWithoutAi'),
                                                total: monthlyPriceFor(p, simPros, simAi),
                                            }))}
                                            data-cy="plan-request-seats"
                                        >
                                            {isCurrent ? t('plan.page.adjustSubscription') : t('plan.page.upgradeTo', { plan: t(`plan.names.${key}`) })}
                                        </a>
                                        <label className="gs-inline-check gs-plan-calc__ai">
                                            <input
                                                type="checkbox"
                                                className="gs-inline-check__box"
                                                checked={simAi}
                                                onChange={(e) => setSimAi(e.target.checked)}
                                                data-cy="plan-calculator-ai"
                                            />
                                            <span>{t('plan.page.calcWithAi', { price: p.aiAddonBrl })}</span>
                                        </label>
                                    </div>
                                    </>
                                )}

                                {/* The paid card's call to action lives in the calculator, next to the
                                    size the owner just simulated — a second button here was a duplicate. */}
                                {isCurrent && (
                                    <span className="gs-plan-card__current">{t('plan.page.currentPlan')}</span>
                                )}
                            </div>
                        );
                    })}

                </div>

                <p className="gs-plan-footnote">{t('plan.page.contact', { email: UPGRADE_EMAIL })}</p>
            </div>
        </div>
    );
};

export default PlanComponent;
